import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { useLocation, useNavigate } from 'react-router'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { AnimatePresence, LayoutGroup, motion, useReducedMotion } from 'motion/react'
import {
  Bell, BriefcaseBusiness, CalendarClock, ChartNoAxesCombined, CheckCheck, Clock3, Cog, Flame, FolderKanban,
  Headset, Home, Settings2, Sparkles, Users, Video, Building2, ArrowRight, Search, ExternalLink, Sunrise, Sun, MoonStar, CloudSun, CheckCircle2, CalendarDays, X, Moon,
} from 'lucide-react'
import { apiGet, apiPost } from '@/api/axios-client'
import { qk } from '@/api/query-keys'
import { useIdentity } from '@/features/permissions/AbilityProvider'
import { paths } from '@/routes/paths'
import { cn } from '@/lib/utils'
import { localTimeZone } from '@/lib/timezone'
import { onAnnounce, type IslandAlert } from './islandBus'
import { showOsNotification } from './osNotifications'
import { greeting, pageContext, type IslandIcon } from './pageContext'
import { useIslandSettings } from './useIslandSettings'
import { playIslandSound, setIslandSoundsEnabled, type IslandSound } from './sounds'
import { briefPeriod, buildBrief, type Brief, type BriefRow } from './briefs'
import { AskPanel } from './AskPanel'
import { addDays, startOfDay, useCalendarEvents, type CalendarEvent } from '@/features/calendar'
import { useWeather } from './useWeather'
import { focusAllowsCategory, useFocusMode, type FocusCategory } from '@/features/focus'

/**
 * The Dynamic Island: a small black pill that floats over the app and quietly keeps you current:
 * the time, the weather, what is due next, new notifications, and a line about your work.
 *
 * It morphs between four shapes with spring physics, the way the iPhone's does:
 *   compact   a pill: time · context · one rotating line
 *   expanded  a card, on click: time + weather, up next, notifications, an insight
 *   alert     a wide pill, briefly, when something arrives (also sent to the OS if enabled)
 *   tucked    a tiny pill while you scroll, so it never sits on what you are reading
 * Where it sits comes from the page (see pageContext): bottom on boards and tables, under the
 * header on reading pages, and it glides between the two when you navigate.
 */

interface IslandTask { id: string; title: string; status: string; dueAt: string | null; overdue: boolean }
interface IslandSupportItem { id: string; reference: string; title: string; breached: boolean; deadlineAt: string | null }
interface IslandPayload {
  tasks: IslandTask[]
  support: IslandSupportItem[]
  counts: { open: number; overdue: number; dueToday: number; dueTomorrow: number; doneToday: number; doneThisWeek: number; supportOpen: number; supportBreached: number }
  facts: string[]
  nextStep: string | null
  /** App path (under /w/<slug>) of the ticket or work item the next step is about. */
  nextStepHref?: string | null
  aiFact: string | null
}

/** How often overdue/breached work is brought back up while it stays that way. */
const NUDGE_EVERY_MS = 10 * 60_000
const FIRST_NUDGE_MS = 45_000
interface InboxItem { id: string; title: string; body: string | null; href: string | null; readAt: string | null; createdAt: string }

type Mode = 'compact' | 'expanded' | 'alert' | 'brief' | 'ask' | 'tucked'

const SPRING = { type: 'spring', stiffness: 420, damping: 34, mass: 0.9 } as const
const SOFT = { type: 'spring', stiffness: 260, damping: 30 } as const

const ICONS: Record<IslandIcon, typeof Home> = {
  work: BriefcaseBusiness, support: Headset, reports: ChartNoAxesCombined, approvals: CheckCheck, meetings: Video,
  overdue: Flame, clients: Building2, projects: FolderKanban, people: Users, settings: Cog, home: Home,
}

function useNow(stepMs: number): Date {
  const [now, setNow] = useState(() => new Date())
  useEffect(() => {
    const t = window.setInterval(() => setNow(new Date()), stepMs)
    return () => window.clearInterval(t)
  }, [stepMs])
  return now
}

const timeFmt = (d: Date) => d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit', timeZone: localTimeZone() })
const dateFmt = (d: Date) => d.toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric', timeZone: localTimeZone() })
function dueLabel(iso: string | null, now: number): string {
  if (!iso) return 'No due date'
  const ms = Date.parse(iso) - now
  const abs = Math.abs(ms)
  const unit = abs < 3_600_000 ? `${Math.max(1, Math.round(abs / 60_000))}m` : abs < 86_400_000 ? `${Math.round(abs / 3_600_000)}h` : `${Math.round(abs / 86_400_000)}d`
  return ms < 0 ? `${unit} overdue` : `due in ${unit}`
}

export function DynamicIsland({ workspaceSlug, onOpenSettings }: { workspaceSlug: string; onOpenSettings: () => void }) {
  const { settings, save } = useIslandSettings()
  // Focus mode: the island's own reminders and nudges respect the same categories as notifications.
  const { focus, active: focusOn } = useFocusMode()
  const focusRef = useRef(focus)
  useEffect(() => {
    focusRef.current = focus
  })
  const allowed = (category: FocusCategory) => focusAllowsCategory(focusRef.current, category)
  setIslandSoundsEnabled(settings.sounds)
  const reduce = useReducedMotion()
  const location = useLocation()
  const navigate = useNavigate()
  const identity = useIdentity()
  const ctx = pageContext(location.pathname)
  const now = useNow(15_000)

  const island = useQuery({
    queryKey: ['island', ctx.key],
    queryFn: () => apiGet<IslandPayload>('island', { page: ctx.label }),
    enabled: settings.enabled,
    refetchInterval: 60_000,
    staleTime: 30_000,
    retry: false,
  })
  const inbox = useQuery({
    queryKey: qk.notifications(),
    queryFn: () => apiGet<{ items: InboxItem[]; unread: number }>('notify/inbox', { limit: 30 }),
    enabled: settings.enabled,
    staleTime: 10_000,
  })
  const weather = useWeather(settings.enabled && settings.weather)

  // Google Calendar (if connected in this browser): today's events for the pill, the card,
  // reminders and the briefs. The range is fixed per day so the query key is stable.
  const dayKey = now.toDateString()
  const [calRange] = useMemo(() => [{ from: startOfDay(now), to: addDays(startOfDay(now), 1) }], [dayKey]) // eslint-disable-line react-hooks/exhaustive-deps
  const calendar = useCalendarEvents(calRange.from, calRange.to, settings.enabled)
  const nowMs = now.getTime()
  const upcoming = calendar.events.filter((e) => !e.allDay && Date.parse(e.end) > nowMs && e.responseStatus !== 'declined')
  const liveEvent = upcoming.find((e) => Date.parse(e.start) <= nowMs) ?? null
  const nextEvent = upcoming.find((e) => Date.parse(e.start) > nowMs) ?? null

  // Clear (mark read) from inside the island: the same endpoint the bell uses, optimistic so the
  // row slides away at once. An empty list means "all", as the server reads it.
  const queryClient = useQueryClient()
  const markRead = (ids: string[]) => {
    const readAt = new Date().toISOString()
    queryClient.setQueryData<{ items: InboxItem[]; unread: number }>(qk.notifications(), (old) =>
      old ? { ...old, items: old.items.map((n) => (!ids.length || ids.includes(n.id) ? { ...n, readAt: n.readAt ?? readAt } : n)), unread: ids.length ? Math.max(0, old.unread - ids.length) : 0 } : old,
    )
    void apiPost('notify/inbox/read', { ids }).finally(() => queryClient.invalidateQueries({ queryKey: qk.notifications() }))
  }

  // The visible shape is derived, not stored: an alert wins, then an open card (which belongs to
  // the page it was opened on, so navigating closes it), then compact/tucked.
  const [base, setBase] = useState<'compact' | 'tucked'>('compact')
  const [expandedAt, setExpandedAt] = useState<string | null>(null)
  const [alert, setAlert] = useState<IslandAlert | null>(null)
  const [brief, setBrief] = useState<Brief | null>(null)
  const expanded = expandedAt === location.pathname
  // Ask wins while it is open: alerts and briefs wait rather than snatching the input away.
  const [askOpen, setAskOpen] = useState(false)
  const mode: Mode = askOpen ? 'ask' : brief ? 'brief' : alert ? 'alert' : expanded ? 'expanded' : base
  const openAsk = () => {
    playIslandSound('open')
    setExpandedAt(null)
    setAskOpen(true)
  }
  const closeAsk = () => {
    playIslandSound('close')
    setAskOpen(false)
  }
  const expand = () => {
    playIslandSound('open')
    setExpandedAt(location.pathname)
  }
  const collapse = () => {
    playIslandSound('close')
    setExpandedAt(null)
  }
  const queue = useRef<IslandAlert[]>([])

  // Alerts: queue them, show each for a few seconds, and mirror to the OS when enabled.
  useEffect(() => onAnnounce((a) => {
    if (settings.osNotifications) {
      void showOsNotification({ title: a.title, body: a.body, url: a.href ? `/w/${workspaceSlug}${a.href}` : null, tag: a.id })
    }
    queue.current.push(a)
    setAlert((current) => current ?? queue.current.shift() ?? null)
  }), [settings.osNotifications, workspaceSlug])
  useEffect(() => {
    if (!alert || askOpen) return
    const sound: IslandSound = alert.tone === 'warning' || alert.tone === 'danger' ? 'warning' : alert.tone === 'success' ? 'success' : 'alert'
    playIslandSound(sound)
    const t = window.setTimeout(() => setAlert(queue.current.shift() ?? null), 5_500)
    return () => window.clearTimeout(t)
  }, [alert, askOpen])

  // Reminders: anything of yours due within 15 minutes is announced once.
  useEffect(() => {
    for (const task of island.data?.tasks ?? []) {
      if (!task.dueAt) continue
      const ms = Date.parse(task.dueAt) - Date.now()
      const key = `tapwork:island:reminded:${task.id}:${task.dueAt}`
      if (ms > 0 && ms <= 15 * 60_000 && !sessionStorage.getItem(key) && allowed('due')) {
        sessionStorage.setItem(key, '1')
        window.dispatchEvent(new CustomEvent('tapwork:island-remind', { detail: task }))
        queue.current.push({ id: `due:${task.id}`, title: `Due in ${Math.max(1, Math.round(ms / 60_000))} min`, body: task.title, href: `/work?card=${task.id}`, tone: 'warning' })
        setAlert((current) => current ?? queue.current.shift() ?? null)
      }
    }
  }, [island.data?.tasks, now])

  // Meeting reminders: 10 minutes before, and as it starts. Each fires once per event and moment.
  useEffect(() => {
    if (!settings.enabled) return
    for (const e of upcoming) {
      const startMs = Date.parse(e.start)
      const mins = Math.round((startMs - Date.now()) / 60_000)
      const stage = mins <= 0 && mins > -2 ? 'start' : mins > 0 && mins <= 10 ? 'soon' : null
      if (!stage || !allowed('meetings')) continue
      const key = `tapwork:island:cal:${e.id}:${e.start}:${stage}`
      if (sessionStorage.getItem(key)) continue
      sessionStorage.setItem(key, '1')
      const a: IslandAlert = {
        id: `cal:${e.id}:${stage}`,
        title: stage === 'start' ? `Starting now · ${e.title}` : `In ${Math.max(1, mins)} min · ${e.title}`,
        body: e.joinUrl ? 'Tap to join' : e.location ?? null,
        url: e.joinUrl ?? e.htmlLink,
        tone: 'success',
      }
      queue.current.push(a)
      if (settings.osNotifications) void showOsNotification({ title: a.title, body: a.body, url: a.url ?? null, tag: a.id })
      window.setTimeout(() => setAlert((current) => current ?? queue.current.shift() ?? null), 0)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- re-evaluated each clock tick
  }, [now, calendar.events, settings.enabled, settings.osNotifications])

  // A partner, not a pager: while anything is overdue or breached, bring ONE of them back up every
  // few minutes, rotating through them, so nothing silently rots, without a wall of alerts.
  const nudgeIndex = useRef(0)
  const nudgeItems = useMemo(() => [
    ...(island.data?.support ?? []).filter((t) => t.breached).map((t): IslandAlert => ({
      id: `nudge:support:${t.id}`, title: `${t.reference} has breached SLA`, body: t.title, tone: 'danger',
      href: `/support-ticket?ticket=${encodeURIComponent(t.id)}`,
    })),
    ...(island.data?.tasks ?? []).filter((t) => t.overdue).map((t): IslandAlert => ({
      id: `nudge:task:${t.id}`, title: 'Still overdue', body: t.title, tone: 'warning',
      href: `/work?card=${t.id}`,
    })),
  ], [island.data?.support, island.data?.tasks])
  const nudgeKey = nudgeItems.map((n) => n.id).join('|')
  useEffect(() => {
    if (!settings.enabled || !nudgeItems.length) return
    const fire = () => {
      const item = nudgeItems[nudgeIndex.current % nudgeItems.length]!
      if (!allowed(item.id.startsWith('nudge:support:') ? 'support' : 'due')) {
        nudgeIndex.current += 1
        return
      }
      nudgeIndex.current += 1
      queue.current.push({ ...item, id: `${item.id}:${Date.now()}` })
      setAlert((current) => current ?? queue.current.shift() ?? null)
    }
    const first = window.setTimeout(fire, FIRST_NUDGE_MS)
    const every = window.setInterval(fire, NUDGE_EVERY_MS)
    return () => {
      window.clearTimeout(first)
      window.clearInterval(every)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on the set of items, not their identity
  }, [nudgeKey, settings.enabled])

  // New assignments: compare what is on your plate between polls. The first load only records
  // what is already there, so nothing old is announced as new.
  const known = useRef<{ support: Set<string>; tasks: Set<string> } | null>(null)
  useEffect(() => {
    const data = island.data
    if (!data) return
    const support = new Set(data.support.map((t) => t.id))
    const tasks = new Set(data.tasks.map((t) => t.id))
    const prev = known.current
    known.current = { support, tasks }
    if (!prev) return
    const fresh: IslandAlert[] = [
      ...data.support.filter((t) => !prev.support.has(t.id)).map((t): IslandAlert => ({
        id: `assigned:support:${t.id}`, title: `L3 ticket assigned to you · ${t.reference}`, body: t.title, tone: t.breached ? 'danger' : 'success',
        href: `/support-ticket?ticket=${encodeURIComponent(t.id)}`,
      })),
      ...data.tasks.filter((t) => !prev.tasks.has(t.id)).map((t): IslandAlert => ({
        id: `assigned:task:${t.id}`, title: 'New work assigned to you', body: t.title, tone: 'success', href: `/work?card=${t.id}`,
      })),
    ]
    const wanted = fresh.filter((a) => allowed(a.id.startsWith('assigned:support:') ? 'support' : 'assignments'))
    if (!wanted.length) return
    for (const a of wanted) {
      queue.current.push(a)
      if (settings.osNotifications) void showOsNotification({ title: a.title, body: a.body, url: a.href ? `/w/${workspaceSlug}${a.href}` : null, tag: a.id })
    }
    const t = window.setTimeout(() => setAlert((current) => current ?? queue.current.shift() ?? null), 0)
    return () => window.clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- runs on each new poll result
  }, [island.data])

  // Morning, afternoon and evening briefs: each shown once, the first time the island has data in
  // that part of the day, and re-openable from the expanded card.
  const makeBrief = (): Brief | null => {
    const c = island.data?.counts
    const period = briefPeriod(new Date())
    if (!c || !period) return null
    return buildBrief({
      period, date: new Date(), name: identity.displayName, counts: c, nextStep: island.data?.nextStep,
      weather: settings.weather ? weather.data ?? null : null,
      topDueToday: island.data?.tasks.find((t) => t.dueAt && new Date(t.dueAt).toDateString() === new Date().toDateString())?.title ?? null,
      meetings: calendar.status === 'connected' ? {
        count: upcoming.length,
        nextTitle: (liveEvent ?? nextEvent)?.title ?? null,
        nextTime: nextEvent ? new Date(nextEvent.start).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit', timeZone: localTimeZone() }) : null,
      } : null,
    })
  }
  const briefSource = island.data?.counts ? JSON.stringify(island.data.counts) : ''
  useEffect(() => {
    if (!settings.briefs || !briefSource) return
    const next = makeBrief()
    if (!next) return
    const key = `tapwork:island:brief:${next.id}`
    if (localStorage.getItem(key)) return
    // A beat after the page settles, so the brief arrives rather than appears.
    const t = window.setTimeout(() => {
      localStorage.setItem(key, '1')
      setBrief(next)
      playIslandSound('brief')
    }, 1_200)
    return () => window.clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once per period, when data first arrives
  }, [briefSource, settings.briefs])
  const [briefHover, setBriefHover] = useState(false)
  useEffect(() => {
    if (!brief || briefHover) return
    const t = window.setTimeout(() => setBrief(null), 14_000)
    return () => window.clearTimeout(t)
  }, [brief, briefHover])

  // Tuck away while scrolling; come back when the page settles.
  useEffect(() => {
    let t: number | undefined
    const onScroll = () => {
      setBase('tucked')
      window.clearTimeout(t)
      t = window.setTimeout(() => setBase('compact'), 900)
    }
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => {
      window.removeEventListener('scroll', onScroll)
      window.clearTimeout(t)
    }
  }, [])

  // Collapse the card on Escape or a click elsewhere (navigation closes it via expandedAt).
  const ref = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (mode !== 'expanded' && mode !== 'ask') return
    const close = () => {
      playIslandSound('close')
      setExpandedAt(null)
      setAskOpen(false)
    }
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && close()
    const onDown = (e: PointerEvent) => !ref.current?.contains(e.target as Node) && close()
    window.addEventListener('keydown', onKey)
    window.addEventListener('pointerdown', onDown)
    return () => {
      window.removeEventListener('keydown', onKey)
      window.removeEventListener('pointerdown', onDown)
    }
  }, [mode])

  // The rotating line in the compact pill: urgent first, then facts, then the page's own hint.
  const unread = (inbox.data?.items ?? []).filter((n) => !n.readAt)
  const next = island.data?.tasks[0]
  const lines = useMemo(() => {
    const out: Array<{ key: string; text: string; icon: ReactNode }> = []
    const c = island.data?.counts
    if (c?.supportBreached) out.push({ key: 'breached', text: `${c.supportBreached} L3 ticket${c.supportBreached === 1 ? '' : 's'} breached`, icon: <Headset className="size-3.5 text-danger" /> })
    if (c?.overdue) out.push({ key: 'overdue', text: `${c.overdue} overdue`, icon: <Flame className="size-3.5 text-danger" /> })
    // Developers: what is on your L3 plate right now, by reference.
    const l3 = island.data?.support ?? []
    if (l3.length) out.push({ key: `l3:${l3.map((t) => t.id).join(',')}`, text: `L3: ${l3.slice(0, 3).map((t) => t.reference).join(', ')}${l3.length > 3 ? ` +${l3.length - 3}` : ''} assigned to you`, icon: <Headset className="size-3.5 text-ios-green" /> })
    if (liveEvent) out.push({ key: `cal-live:${liveEvent.id}`, text: `Now: ${liveEvent.title}`, icon: <Video className="size-3.5 text-ios-green" /> })
    else if (nextEvent) {
      const mins = Math.round((Date.parse(nextEvent.start) - nowMs) / 60_000)
      const when = mins < 60 ? `in ${Math.max(1, mins)} min` : `at ${new Date(nextEvent.start).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit', timeZone: localTimeZone() })}`
      out.push({ key: `cal-next:${nextEvent.id}:${when}`, text: `${nextEvent.title} ${when}`, icon: <CalendarClock className="size-3.5 text-ios-green" /> })
    }
    if (island.data?.nextStep) out.push({ key: `step:${island.data.nextStep}`, text: island.data.nextStep, icon: <ArrowRight className="size-3.5 text-success" /> })
    if (unread.length) out.push({ key: 'unread', text: `${unread.length} new notification${unread.length === 1 ? '' : 's'}`, icon: <Bell className="size-3.5 text-info" /> })
    if (next) out.push({ key: `next:${next.id}`, text: `Next: ${next.title}`, icon: <CalendarClock className="size-3.5 text-brand-accent" /> })
    if (weather.data) out.push({ key: 'weather', text: `${weather.data.emoji} ${weather.data.temperatureC}° ${weather.data.label}`, icon: null })
    for (const f of island.data?.facts ?? []) out.push({ key: `f:${f}`, text: f, icon: <Sparkles className="size-3.5 text-brand-accent" /> })
    if (settings.aiFacts && island.data?.aiFact) out.push({ key: 'ai', text: island.data.aiFact, icon: <Sparkles className="size-3.5 text-brand-accent" /> })
    out.push({ key: `hint:${ctx.key}`, text: ctx.hint, icon: null })
    return out
  }, [island.data, unread.length, next, weather.data, settings.aiFacts, ctx.key, ctx.hint, liveEvent, nextEvent, nowMs])

  const [tick, setTick] = useState(0)
  const [hover, setHover] = useState(false)
  useEffect(() => {
    if (hover || mode !== 'compact') return
    const t = window.setInterval(() => setTick((n) => n + 1), 6_000)
    return () => window.clearInterval(t)
  }, [hover, mode])
  const line = lines[tick % Math.max(1, lines.length)]

  const urgent = Boolean(island.data?.counts.overdue || island.data?.counts.supportBreached)
  // The current period's brief, for the featured tile in the expanded card.
  const preview = mode === 'expanded' ? makeBrief() : null
  const topOffset = useTopOffset(location.pathname)
  const desktop = useIsDesktop()
  const slot = useIslandSlot(desktop && !settings.position)

  // Long-press, then drag it anywhere, the way iOS lets you pick up an icon: after a short hold it
  // lifts, then follows the pointer 1:1 from the exact spot you grabbed (no springs while moving;
  // springs are only for the lift and the drop). The spot is saved as fractions of the window so
  // it holds across screen sizes, and automatic placement steps aside until it is reset.
  const [dragCenter, setDragCenter] = useState<{ x: number; y: number } | null>(null)
  const [dragging, setDragging] = useState(false)
  const press = useRef<{ x: number; y: number; timer: number; moved: boolean; dx: number; dy: number; el: HTMLElement; pointerId: number } | null>(null)
  const suppressClick = useRef(false)
  const saved = settings.position
  const clampX = (v: number) => Math.min(window.innerWidth - 24, Math.max(24, v))
  const clampY = (v: number) => Math.min(window.innerHeight - 24, Math.max(24, v))
  const pos = dragCenter
    ? dragCenter
    : saved && typeof window !== 'undefined'
      ? { x: saved.x * window.innerWidth, y: saved.y * window.innerHeight }
      : null
  const onPointerDown = (e: React.PointerEvent<HTMLElement>) => {
    if (e.button !== 0 || mode === 'expanded' || mode === 'brief') return
    const el = e.currentTarget
    const pointerId = e.pointerId
    const timer = window.setTimeout(() => {
      const p = press.current
      if (!p || p.moved) return
      // Keep the grab point: the island's centre stays at the same offset from the pointer.
      const r = el.getBoundingClientRect()
      p.dx = p.x - (r.left + r.width / 2)
      p.dy = p.y - (r.top + r.height / 2)
      try {
        el.setPointerCapture(pointerId)
      } catch {
        // Capture is a nicety; dragging still works within the window.
      }
      setDragCenter({ x: r.left + r.width / 2, y: r.top + r.height / 2 })
      setDragging(true)
      playIslandSound('pickup')
    }, 350)
    press.current = { x: e.clientX, y: e.clientY, timer, moved: false, dx: 0, dy: 0, el, pointerId }
  }
  const onPointerMove = (e: React.PointerEvent) => {
    const p = press.current
    if (!p) return
    if (!dragging) {
      if (Math.hypot(e.clientX - p.x, e.clientY - p.y) > 6) {
        p.moved = true
        window.clearTimeout(p.timer)
      }
      return
    }
    e.preventDefault()
    setDragCenter({ x: clampX(e.clientX - p.dx), y: clampY(e.clientY - p.dy) })
  }
  const endPress = () => {
    const p = press.current
    if (p) window.clearTimeout(p.timer)
    press.current = null
    if (!dragging) return
    setDragging(false)
    suppressClick.current = true
    playIslandSound('drop')
    if (dragCenter) {
      const next = { x: dragCenter.x / window.innerWidth, y: dragCenter.y / window.innerHeight }
      void save({ position: next }).finally(() => setDragCenter(null))
    }
  }

  if (!settings.enabled) return null
  const Icon = ICONS[ctx.icon]
  const firstName = (identity.displayName || '').split(' ')[0]

  return (
    <div
      aria-live="polite"
      className={cn(
        'pointer-events-none fixed',
        pos
          ? 'left-0'
          : desktop && slot
            // Desktop default: centred in the top bar's free middle, growing downward.
            ? 'flex justify-center'
            : desktop
              // No room in the top bar (narrow window): tucked into the bottom-right corner.
              ? 'bottom-5 right-5 flex justify-end'
              : 'inset-x-0 flex justify-center px-3',
        !pos && !desktop && ctx.placement === 'bottom' && 'bottom-5',
      )}
      style={{
        zIndex: 65,
        ...(pos
          ? anchoredStyle(pos, dragging)
          : desktop && slot
            ? { left: slot.left, width: slot.width, top: slot.top + Math.max(0, (slot.height - COMPACT_H) / 2) }
            : !desktop && ctx.placement === 'top' ? { top: topOffset } : {}),
      }}
    >
      <LayoutGroup>
        <motion.div
          ref={ref}
          layout={!dragging}
          transition={reduce ? { duration: 0 } : SPRING}
          initial={{ opacity: 0, y: (!desktop && ctx.placement === 'top') || (desktop && slot) ? -16 : 16, scale: 0.9 }}
          onHoverStart={() => setHover(true)}
          onHoverEnd={() => setHover(false)}
          onClick={() => {
            if (suppressClick.current) {
              suppressClick.current = false
              return
            }
            if (mode === 'compact' || mode === 'tucked') expand()
          }}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={endPress}
          onPointerCancel={endPress}
          onContextMenu={(e) => dragging && e.preventDefault()}
          role={mode === 'expanded' || mode === 'ask' ? 'dialog' : 'button'}
          aria-label={mode === 'ask' ? 'Ask TapWork' : mode === 'expanded' ? 'Dynamic Island' : 'Open Dynamic Island'}
          tabIndex={0}
          onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && (mode === 'compact' || mode === 'tucked') && expand()}
          style={{
            borderRadius: mode === 'expanded' || mode === 'brief' || mode === 'ask' ? 28 : 999,
            touchAction: dragging ? 'none' : undefined,
            // Resting in the top bar, anything that appears on its own stays inside the free gap;
            // only what the person opens (the card, Ask) may spread over the page.
            ...(slot && !pos && (mode === 'compact' || mode === 'tucked' || mode === 'alert' || mode === 'brief') ? { maxWidth: slot.width - 8 } : {}),
          }}
          whileTap={dragging ? undefined : { scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: dragging ? 1.06 : 1 }}
          className={cn(
            'pointer-events-auto relative overflow-hidden bg-black text-white shadow-[0_12px_40px_-12px_rgba(0,0,0,0.55)] ring-1 ring-white/10',
            // Something needs attention: a slow red breathing ring, so the pill reads as alive.
            urgent && mode === 'compact' && 'ring-2 ring-danger/60 motion-safe:animate-[tt-urgent_2.4s_ease-in-out_infinite]',
            'cursor-pointer select-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-accent',
            mode === 'expanded' && 'w-[min(26rem,calc(100vw-1.5rem))] cursor-default',
            mode === 'alert' && 'w-[min(24rem,calc(100vw-1.5rem))] ring-2 ring-ios-green/70',
            mode === 'brief' && 'w-[min(25rem,calc(100vw-1.5rem))] cursor-default',
            mode === 'ask' && 'w-[min(27rem,calc(100vw-1.5rem))] cursor-default',
            dragging && 'cursor-grabbing shadow-[0_24px_60px_-12px_rgba(0,0,0,0.6)]',
          )}
        >
          <AnimatePresence mode="popLayout" initial={false}>
            {mode === 'ask' ? (
              <motion.div key="ask" layout={dragging ? false : 'position'} className="w-full" {...fade(reduce)}>
                <AskPanel pageLabel={ctx.label} onClose={closeAsk} reduce={reduce} />
              </motion.div>
            ) : mode === 'tucked' ? (
              <motion.div key="tucked" layout={dragging ? false : 'position'} className="flex h-8 items-center gap-2 px-3" {...fade(reduce)}>
                <span className="size-1.5 rounded-full bg-success motion-safe:animate-pulse" />
                <span className="num text-xs font-semibold">{timeFmt(now)}</span>
              </motion.div>
            ) : mode === 'brief' && brief ? (
              <motion.div
                key={`brief:${brief.id}`}
                layout={dragging ? false : 'position'}
                className="relative w-full overflow-hidden"
                onHoverStart={() => setBriefHover(true)}
                onHoverEnd={() => setBriefHover(false)}
                {...fade(reduce)}
              >
                {/* Green header band: the brief is the island's headline moment. */}
                <div className="relative overflow-hidden bg-gradient-to-br from-ios-green via-success to-ios-green/70 px-5 pb-5 pt-5">
                  <BriefShimmer reduce={reduce} />
                  <div className="relative flex items-start gap-3">
                    <motion.span
                      className="grid size-11 shrink-0 place-items-center rounded-2xl bg-white/25 shadow-sm backdrop-blur"
                      initial={reduce ? false : { scale: 0.4, rotate: -40 }}
                      animate={{ scale: 1, rotate: 0 }}
                      transition={{ type: 'spring', stiffness: 380, damping: 18 }}
                    >
                      <PeriodIcon period={brief.period} className="size-6 text-white" />
                    </motion.span>
                    <div className="min-w-0 flex-1">
                      <motion.p
                        className="text-xl font-semibold tracking-tight text-white"
                        initial={reduce ? false : { opacity: 0, y: 6 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ ...SOFT, delay: 0.05 }}
                      >
                        {brief.title}
                      </motion.p>
                      <motion.p
                        className="mt-0.5 text-sm text-white/85"
                        initial={reduce ? false : { opacity: 0, y: 6 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ ...SOFT, delay: 0.1 }}
                      >
                        {brief.subtitle}
                      </motion.p>
                    </div>
                    <button type="button" aria-label="Dismiss brief" className="rounded-full p-1 text-white/70 hover:bg-white/20 hover:text-white" onClick={(e) => { e.stopPropagation(); playIslandSound('close'); setBrief(null) }}>
                      <X className="size-4" />
                    </button>
                  </div>
                </div>
                <ul className="grid gap-2 p-4">
                  {brief.rows.map((row, i) => (
                    <motion.li
                      key={`${row.kind}:${i}`}
                      className="flex items-start gap-3 rounded-2xl bg-white/[0.07] px-3.5 py-3 text-[15px] leading-snug"
                      initial={reduce ? false : { opacity: 0, y: 14, scale: 0.96 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      transition={{ type: 'spring', stiffness: 320, damping: 26, delay: 0.16 + i * 0.07 }}
                    >
                      <motion.span
                        className="mt-0.5 grid size-6 shrink-0 place-items-center rounded-full bg-white/10"
                        initial={reduce ? false : { scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ type: 'spring', stiffness: 500, damping: 20, delay: 0.22 + i * 0.07 }}
                      >
                        <BriefIcon kind={row.kind} />
                      </motion.span>
                      <span className="pt-0.5">{row.text}</span>
                    </motion.li>
                  ))}
                </ul>
              </motion.div>
            ) : mode === 'alert' && alert ? (
              <motion.button
                key={`alert:${alert.id}`}
                type="button"
                layout={dragging ? false : 'position'}
                className="flex w-full items-center gap-3 px-4 py-3 text-left"
                onClick={(e) => {
                  e.stopPropagation()
                  if (alert.url) window.open(alert.url, '_blank', 'noopener,noreferrer')
                  else if (alert.href) navigate(`/w/${workspaceSlug}${alert.href}`)
                  setAlert(queue.current.shift() ?? null)
                }}
                {...fade(reduce)}
              >
                <motion.span
                  className={cn('grid size-9 shrink-0 place-items-center rounded-full', alert.tone === 'warning' ? 'bg-warning text-white' : alert.tone === 'danger' ? 'bg-danger text-white' : 'bg-ios-green text-white')}
                  initial={reduce ? false : { scale: 0.4, rotate: -20 }}
                  animate={{ scale: 1, rotate: 0 }}
                  transition={SOFT}
                >
                  {alert.tone === 'warning' ? <Clock3 className="size-4" /> : alert.tone === 'success' ? <CheckCircle2 className="size-4" /> : <Bell className="size-4" />}
                </motion.span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-semibold">{alert.title}</span>
                  {alert.body ? <span className="block truncate text-xs text-white/65">{alert.body}</span> : null}
                </span>
                <span className="shrink-0 text-tiny text-white/50">now</span>
              </motion.button>
            ) : mode === 'expanded' ? (
              <motion.div
                key="expanded"
                layout={dragging ? false : 'position'}
                // Never taller than the screen allows: header and footer stay put, the middle scrolls.
                className="flex max-h-[min(34rem,calc(100dvh-7rem))] flex-col"
                {...fade(reduce)}
              >
                <div className="flex shrink-0 items-start justify-between gap-3 px-5 pt-5">
                  <div>
                    <p className="text-xs text-white/55">{greeting(now)}{firstName ? `, ${firstName}` : ''}</p>
                    <p className="num text-3xl font-semibold tracking-tight">{timeFmt(now)}</p>
                    <p className="text-xs text-white/55">{dateFmt(now)}</p>
                  </div>
                  <div className="flex items-start gap-2">
                  <motion.button
                    type="button"
                    aria-label="Ask about your work"
                    onClick={(e) => { e.stopPropagation(); openAsk() }}
                    whileHover={reduce ? undefined : { scale: 1.06 }}
                    whileTap={{ scale: 0.92 }}
                    transition={SOFT}
                    className="grid size-9 place-items-center rounded-full bg-white text-black shadow-sm"
                  >
                    <Search className="size-4" strokeWidth={3} />
                  </motion.button>
                  {weather.data ? (
                    <div className="rounded-2xl bg-white/8 px-3 py-2 text-right">
                      <p className="text-2xl leading-none">{weather.data.emoji}</p>
                      <p className="num mt-1 text-sm font-semibold">{weather.data.temperatureC}°C</p>
                      <p className="text-tiny text-white/55">{weather.data.label}</p>
                    </div>
                  ) : null}
                  </div>
                </div>

                {settings.briefs && preview ? (
                  <motion.button
                    type="button"
                    initial={reduce ? false : { opacity: 0, y: 8, scale: 0.97 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    transition={{ ...SOFT, delay: 0.02 }}
                    whileHover={reduce ? undefined : { y: -1 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={(e) => { e.stopPropagation(); setExpandedAt(null); setBrief(preview); playIslandSound('brief') }}
                    className="relative mx-5 mt-4 flex shrink-0 items-center gap-3 overflow-hidden rounded-2xl bg-gradient-to-br from-ios-green via-success to-ios-green/70 px-4 py-3 text-left text-white shadow-[0_10px_30px_-12px_var(--color-ios-green)]"
                  >
                    <BriefShimmer reduce={reduce} />
                    <span className="relative grid size-9 shrink-0 place-items-center rounded-xl bg-white/25"><PeriodIcon period={preview.period} className="size-5 text-white" /></span>
                    <span className="relative min-w-0 flex-1">
                      <span className="block text-base font-semibold leading-tight">{preview.title}</span>
                      <span className="block truncate text-xs text-white/85">{preview.rows.slice(0, 2).map((r) => r.text).join(' · ')}</span>
                    </span>
                    <ArrowRight className="relative size-4 shrink-0 text-white/90" />
                  </motion.button>
                ) : null}

                <div className="scrollbar-none min-h-0 flex-1 overflow-y-auto overflow-x-hidden overscroll-contain px-5 pb-1">
                  {/* L3 support first: for developers it is the most time-critical thing here. */}
                  {island.data?.support.length ? (
                    <Section scrollX title={`L3 support · ${island.data.counts.supportOpen}${island.data.counts.supportBreached ? ` · ${island.data.counts.supportBreached} breached` : ''}`} delay={0.02} reduce={reduce}>
                      {island.data.support.map((t) => (
                        <Row key={t.id} onClick={() => navigate(paths.supportTicketQueueItem(workspaceSlug, t.id))}>
                          <span className={cn('size-1.5 shrink-0 rounded-full', t.breached ? 'bg-danger motion-safe:animate-pulse' : 'bg-warning')} />
                          <span className="shrink-0 text-tiny text-white/50">{t.reference}</span>
                          <ScrollText>{t.title}</ScrollText>
                          <span className={cn('shrink-0 text-tiny', t.breached ? 'text-danger' : 'text-white/50')}>{t.breached ? 'breached' : dueLabel(t.deadlineAt, now.getTime())}</span>
                        </Row>
                      ))}
                    </Section>
                  ) : null}

                  {island.data?.nextStep ? (
                    <Section title="Suggested next step" delay={0.03} reduce={reduce}>
                      <button
                        type="button"
                        disabled={!island.data.nextStepHref}
                        onClick={(e) => {
                          e.stopPropagation()
                          if (!island.data?.nextStepHref) return
                          playIslandSound('tap')
                          navigate(`/w/${workspaceSlug}${island.data.nextStepHref}`)
                        }}
                        className="group flex w-full gap-2 rounded-xl bg-white/8 px-3 py-2 text-left text-sm leading-snug transition-[background-color,transform] hover:bg-white/14 active:scale-[0.99] disabled:cursor-default disabled:hover:bg-white/8"
                      >
                        <ArrowRight className="mt-0.5 size-4 shrink-0 text-success transition-transform group-hover:translate-x-0.5" />
                        <span className="flex-1">{island.data.nextStep}</span>
                        {island.data.nextStepHref ? <span className="shrink-0 self-center text-tiny text-white/45 group-hover:text-white/80">Open</span> : null}
                      </button>
                    </Section>
                  ) : null}

                  {upcoming.length ? (
                    <Section scrollX title={`Today’s calendar · ${upcoming.length}`} delay={0.035} reduce={reduce}>
                      {upcoming.slice(0, 4).map((e) => <CalendarRow key={e.id} e={e} live={e === liveEvent} />)}
                    </Section>
                  ) : null}

                  <Section scrollX title="Up next" delay={0.04} reduce={reduce}>
                    {island.data?.tasks.length ? island.data.tasks.map((t) => (
                      <Row key={t.id} onClick={() => navigate(paths.workItem(workspaceSlug, t.id))}>
                        <span className={cn('size-1.5 shrink-0 rounded-full', t.overdue ? 'bg-danger' : 'bg-brand-accent')} />
                        <ScrollText>{t.title}</ScrollText>
                        <span className={cn('shrink-0 text-tiny', t.overdue ? 'text-danger' : 'text-white/50')}>{dueLabel(t.dueAt, now.getTime())}</span>
                      </Row>
                    )) : <p className="text-xs text-white/50">Nothing open is assigned to you.</p>}
                  </Section>

                  <Section
                    scrollX
                    title={`Notifications${unread.length ? ` · ${unread.length} new` : ''}`}
                    delay={0.06}
                    reduce={reduce}
                    action={unread.length ? (
                      <button
                        type="button"
                        className="rounded-full px-2 py-0.5 text-tiny font-medium normal-case tracking-normal text-white/60 transition-colors hover:bg-white/10 hover:text-white"
                        onClick={(e) => { e.stopPropagation(); playIslandSound('close'); markRead([]) }}
                      >
                        Clear all
                      </button>
                    ) : null}
                  >
                    <AnimatePresence initial={false}>
                      {unread.length ? unread.slice(0, 8).map((n) => (
                        <motion.div
                          key={n.id}
                          layout={dragging ? false : 'position'}
                          initial={false}
                          exit={reduce ? { opacity: 0 } : { opacity: 0, x: 24, height: 0, transition: { duration: 0.22, ease: [0.32, 0.72, 0, 1] } }}
                          className="group flex items-center gap-1 rounded-xl transition-colors hover:bg-white/10"
                        >
                          <button
                            type="button"
                            className="flex min-w-0 flex-1 items-center gap-2 px-2 py-1.5 text-left text-sm"
                            onClick={(e) => {
                              e.stopPropagation()
                              playIslandSound('tap')
                              markRead([n.id])
                              if (n.href) navigate(`/w/${workspaceSlug}${n.href}`)
                            }}
                          >
                            <Bell className="size-3.5 shrink-0 text-info" />
                            <ScrollText>{n.title}</ScrollText>
                          </button>
                          <button
                            type="button"
                            aria-label={`Clear “${n.title}”`}
                            className="mr-1 grid size-6 shrink-0 place-items-center rounded-full text-white/40 opacity-0 transition-opacity hover:bg-white/15 hover:text-white focus-visible:opacity-100 group-hover:opacity-100"
                            onClick={(e) => { e.stopPropagation(); playIslandSound('close'); markRead([n.id]) }}
                          >
                            <X className="size-3.5" />
                          </button>
                        </motion.div>
                      )) : <p className="text-xs text-white/50">You’re all caught up.</p>}
                    </AnimatePresence>
                  </Section>

                  {settings.aiFacts && island.data?.aiFact ? (
                    <Section title="Insight" delay={0.08} reduce={reduce}>
                      <p className="flex gap-2 text-xs leading-relaxed text-white/80"><Sparkles className="mt-0.5 size-3.5 shrink-0 text-brand-accent" />{island.data.aiFact}</p>
                    </Section>
                  ) : null}
                </div>

                <div className="mx-5 mb-4 mt-2 flex shrink-0 items-center justify-between border-t border-white/10 pt-3 text-tiny text-white/50">
                  <span className="flex min-w-0 items-center gap-1.5"><Icon className="size-3.5 shrink-0" /> <span className="truncate">{ctx.label}{island.data ? ` · ${island.data.counts.open} open · ${island.data.counts.doneThisWeek} done this week` : ''}</span></span>
                  <span className="flex shrink-0 items-center gap-1">
                  <button type="button" className="flex items-center gap-1 rounded-full px-2 py-1 hover:bg-white/10" onClick={(e) => { e.stopPropagation(); collapse(); onOpenSettings() }}>
                    <Settings2 className="size-3.5" /> Settings
                  </button>
                  </span>
                </div>
              </motion.div>
            ) : (
              <motion.div key="compact" layout={dragging ? false : 'position'} className="flex h-10 max-w-[min(32rem,calc(100vw-1.5rem))] items-center gap-2.5 pl-3 pr-1.5" {...fade(reduce)}>
                <motion.span layoutId={dragging ? undefined : 'island-icon'} className="grid size-6 shrink-0 place-items-center rounded-full bg-white/10">
                  <Icon className="size-3.5" />
                </motion.span>
                <span className="num shrink-0 text-xs font-semibold">{timeFmt(now)}</span>
                {focusOn ? <span title="Focus mode is on" className="flex shrink-0 items-center gap-1 rounded-full bg-white/12 px-1.5 py-0.5 text-tiny font-semibold text-white/85"><Moon className="size-3" fill="currentColor" /> Focus</span> : null}
                <span className="h-3 w-px shrink-0 bg-white/20" />
                <span className="relative min-w-0 overflow-hidden">
                  <AnimatePresence mode="wait" initial={false}>
                    <motion.span
                      key={line?.key ?? 'empty'}
                      className="flex min-w-0 items-center gap-1.5 whitespace-nowrap text-xs text-white/85"
                      initial={reduce ? false : { opacity: 0, y: 10, filter: 'blur(4px)' }}
                      animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
                      exit={reduce ? { opacity: 0 } : { opacity: 0, y: -10, filter: 'blur(4px)' }}
                      transition={{ duration: 0.32, ease: [0.32, 0.72, 0, 1] }}
                    >
                      {line?.icon}
                      <span className="truncate">{line?.text ?? ctx.label}</span>
                    </motion.span>
                  </AnimatePresence>
                </span>
                {unread.length ? <span className="ml-1 size-2 shrink-0 rounded-full bg-danger motion-safe:animate-pulse" aria-label={`${unread.length} unread`} /> : null}
                <motion.button
                  type="button"
                  aria-label="Ask about your work"
                  onClick={(e) => { e.stopPropagation(); openAsk() }}
                  onPointerDown={(e) => e.stopPropagation()}
                  whileHover={reduce ? undefined : { scale: 1.08 }}
                  whileTap={{ scale: 0.9 }}
                  transition={SOFT}
                  className="ml-3 grid size-7 shrink-0 place-items-center rounded-full bg-white text-black shadow-sm"
                >
                  <Search className="size-3.5" strokeWidth={3} />
                </motion.button>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </LayoutGroup>
    </div>
  )
}

function fade(reduce: boolean | null) {
  return {
    initial: reduce ? false : { opacity: 0, scale: 0.96, filter: 'blur(6px)' },
    animate: { opacity: 1, scale: 1, filter: 'blur(0px)' },
    exit: reduce ? { opacity: 0 } : { opacity: 0, scale: 0.96, filter: 'blur(6px)' },
    transition: { duration: 0.28, ease: [0.32, 0.72, 0, 1] as [number, number, number, number] },
  } as const
}

/**
 * A titled block. With `scrollX`, its rows scroll sideways together as one strip (trackpad or
 * shift-wheel), independently of every other section, while the heading stays still. Text
 * sections leave it off and wrap normally.
 */
function Section({ title, children, delay, reduce, action, scrollX = false }: { title: string; children: ReactNode; delay: number; reduce: boolean | null; action?: ReactNode; scrollX?: boolean }) {
  return (
    <motion.div
      className="mt-4"
      initial={reduce ? false : { opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ ...SOFT, delay }}
    >
      <div className="mb-1.5 flex items-center justify-between gap-2">
        <p className="text-tiny font-semibold uppercase tracking-wider text-white/40">{title}</p>
        {action}
      </div>
      {scrollX ? (
        <div className="scrollbar-none -mx-1 overflow-x-auto overflow-y-hidden overscroll-x-contain px-1">
          <div className="grid w-max min-w-full gap-0.5">{children}</div>
        </div>
      ) : (
        <div className="grid min-w-0 gap-0.5">{children}</div>
      )}
    </motion.div>
  )
}

function Row({ children, onClick }: { children: ReactNode; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); playIslandSound('tap'); onClick() }}
      className="flex w-full items-center gap-2 rounded-xl px-2 py-1.5 text-left text-sm transition-colors hover:bg-white/10 active:scale-[0.99]"
    >
      {children}
    </button>
  )
}

/**
 * Where "top" is on this page: just under the header, or under any banner marked
 * `data-island-avoid` (the extension prompt, notices) so the island never covers one.
 * Re-measured when banners appear, disappear or resize, and when the page changes.
 */
function useTopOffset(pathname: string): number {
  const [top, setTop] = useState(70)
  useEffect(() => {
    const measure = () => {
      const header = document.querySelector('header')
      let bottom = header ? header.getBoundingClientRect().bottom : 60
      document.querySelectorAll('[data-island-avoid]').forEach((el) => {
        const r = el.getBoundingClientRect()
        if (r.height > 0 && r.top < 240) bottom = Math.max(bottom, r.bottom)
      })
      setTop(Math.round(bottom + 10))
    }
    // At most one layout read per frame, however many mutations or scroll events arrive.
    let frame = 0
    const schedule = () => {
      if (frame) return
      frame = window.requestAnimationFrame(() => {
        frame = 0
        measure()
      })
    }
    measure()
    const ro = new ResizeObserver(schedule)
    document.querySelectorAll('header, [data-island-avoid]').forEach((el) => ro.observe(el))
    // Banners mount and unmount as direct children of the shell, so watching child lists is enough.
    const mo = new MutationObserver(schedule)
    mo.observe(document.body, { childList: true, subtree: true })
    window.addEventListener('scroll', schedule, { passive: true })
    return () => {
      window.cancelAnimationFrame(frame)
      ro.disconnect()
      mo.disconnect()
      window.removeEventListener('scroll', schedule)
    }
  }, [pathname])
  return top
}

/** A slow diagonal sheen across the green, so the brief reads as alive (motion-safe). */
function BriefShimmer({ reduce }: { reduce: boolean | null }) {
  if (reduce) return null
  return (
    <motion.span
      aria-hidden
      className="pointer-events-none absolute inset-y-0 -left-1/2 w-1/2 -skew-x-12 bg-gradient-to-r from-transparent via-white/25 to-transparent"
      initial={{ x: '-20%' }}
      animate={{ x: '320%' }}
      transition={{ duration: 2.6, ease: [0.32, 0.72, 0, 1], repeat: Infinity, repeatDelay: 3.2 }}
    />
  )
}

function PeriodIcon({ period, className }: { period: Brief['period']; className?: string }) {
  if (period === 'morning') return <Sunrise className={className} />
  if (period === 'afternoon') return <Sun className={className} />
  return <MoonStar className={className} />
}

function BriefIcon({ kind }: { kind: BriefRow['kind'] }) {
  const cls = 'mt-0.5 size-4 shrink-0'
  switch (kind) {
    case 'due': return <CalendarClock className={cn(cls, 'text-brand-accent')} />
    case 'overdue': return <Flame className={cn(cls, 'text-danger')} />
    case 'support': return <Headset className={cn(cls, 'text-warning')} />
    case 'done': return <CheckCircle2 className={cn(cls, 'text-ios-green')} />
    case 'tomorrow': return <CalendarDays className={cn(cls, 'text-info')} />
    case 'weather': return <CloudSun className={cn(cls, 'text-warning')} />
    case 'step': return <ArrowRight className={cn(cls, 'text-ios-green')} />
    case 'meetings': return <Video className={cn(cls, 'text-info')} />
    default: return <Sparkles className={cn(cls, 'text-ios-green')} />
  }
}

/** Height of the compact pill (h-10); the anchor keeps ITS centre where the person dropped it. */
const COMPACT_H = 40

/**
 * Where a dragged island sits, and which way it grows.
 *
 * Like the iPhone's island, it opens away from the nearest screen edge: in the top half its top
 * edge stays put and it grows downward; in the bottom half its bottom edge stays put and it grows
 * upward. Horizontally it stays centred on the drop point but is clamped 12px inside the window,
 * so a card opened near a side never runs off screen. While dragging it is simply centred on the
 * pointer's grab point (see onPointerMove), with no transition, so it tracks 1:1.
 */
function anchoredStyle(pos: { x: number; y: number }, dragging: boolean): React.CSSProperties {
  const x = `translateX(clamp(${12 - pos.x}px, -50%, calc(100vw - ${pos.x}px - 100% - 12px)))`
  if (dragging) {
    return { top: 0, transform: `translate3d(calc(${pos.x}px - 50%), calc(${pos.y}px - 50%), 0)`, transition: 'none', willChange: 'transform' }
  }
  const bottomHalf = pos.y > window.innerHeight / 2
  const edge = bottomHalf
    ? { bottom: Math.max(12, window.innerHeight - (pos.y + COMPACT_H / 2)) }
    : { top: Math.max(12, pos.y - COMPACT_H / 2) }
  // No transition: the drop hands over from pointer-centred to edge-anchored at the same spot.
  return { ...edge, transform: `${x} translateX(${pos.x}px)` }
}

/** A title kept on one line; long ones are read by scrolling the whole section sideways. */
function ScrollText({ children }: { children: ReactNode }) {
  return <span className="flex-1 whitespace-nowrap">{children}</span>
}

/**
 * The island's desktop home: the free middle of the top bar (`[data-island-slot]`), between the
 * search and the actions. Re-measured as the window, the sidebar or the actions change size, so
 * the resting pill never covers anything. Null when there is no room (then the corner is used).
 */
function useIslandSlot(enabled: boolean): { left: number; top: number; width: number; height: number } | null {
  const [rect, setRect] = useState<{ left: number; top: number; width: number; height: number } | null>(null)
  useEffect(() => {
    if (!enabled) return
    let el: Element | null = null
    let frame = 0
    const measure = () => {
      el ??= document.querySelector('[data-island-slot]')
      const r = el?.getBoundingClientRect()
      setRect((prev) => {
        if (!r || r.width < 200) return null
        const next = { left: Math.round(r.left), top: Math.round(r.top), width: Math.round(r.width), height: Math.round(r.height) }
        return prev && prev.left === next.left && prev.top === next.top && prev.width === next.width && prev.height === next.height ? prev : next
      })
    }
    const schedule = () => {
      cancelAnimationFrame(frame)
      frame = requestAnimationFrame(measure)
    }
    measure()
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(schedule) : null
    const attach = () => {
      el = document.querySelector('[data-island-slot]')
      if (el) ro?.observe(el)
    }
    attach()
    // The slot mounts with the shell; pick it up if the island arrived first.
    const retry = window.setTimeout(() => {
      if (!el) attach()
      schedule()
    }, 300)
    window.addEventListener('resize', schedule)
    return () => {
      cancelAnimationFrame(frame)
      window.clearTimeout(retry)
      ro?.disconnect()
      window.removeEventListener('resize', schedule)
    }
  }, [enabled])
  return enabled ? rect : null
}

/** md breakpoint and up. Desktop gets the top bar slot; phones keep per-page top/bottom placement. */
function useIsDesktop(): boolean {
  const query = '(min-width: 768px)'
  const [desktop, setDesktop] = useState(() => typeof window !== 'undefined' && window.matchMedia(query).matches)
  useEffect(() => {
    const mq = window.matchMedia(query)
    const onChange = () => setDesktop(mq.matches)
    mq.addEventListener('change', onChange)
    return () => mq.removeEventListener('change', onChange)
  }, [])
  return desktop
}

/** One calendar event in the expanded card: time, title, and a Join button for video calls. */
function CalendarRow({ e, live }: { e: CalendarEvent; live: boolean }) {
  const t = new Date(e.start).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit', timeZone: localTimeZone() })
  const open = e.joinUrl ?? e.htmlLink
  return (
    <div className="flex items-center gap-2 rounded-xl px-2 py-1.5 text-sm">
      <span className={cn('size-1.5 shrink-0 rounded-full', live ? 'bg-ios-green motion-safe:animate-pulse' : 'bg-info')} />
      <span className="num w-16 shrink-0 text-tiny text-white/55">{live ? 'Now' : t}</span>
      <span className="flex-1 whitespace-nowrap">{e.title}</span>
      {open ? (
        <a
          href={open}
          target="_blank"
          rel="noreferrer"
          onClick={(ev) => { ev.stopPropagation(); playIslandSound('tap') }}
          className={cn('flex shrink-0 items-center gap-1 rounded-full px-2.5 py-1 text-tiny font-semibold transition-colors', e.joinUrl ? 'bg-ios-green text-white hover:bg-ios-green/90' : 'bg-white/10 text-white/80 hover:bg-white/20')}
        >
          {e.joinUrl ? <><Video className="size-3" /> Join</> : <><ExternalLink className="size-3" /> Open</>}
        </a>
      ) : null}
    </div>
  )
}

